import { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router';
import IdleTimer from '@components/IdleTimer';
import Spinner from '@components/Spinner/Spinner';
import './App.css';
import { ROUTES } from './config/routes-config';
import { AuthProvider } from './context/AuthContext';
import { MemberProvider } from './context/MemberContext';
import { SpinnerProvider } from './context/SpinnerContext';
import { ConfigProvider } from '@components/ConfigProvider';
import { ThemeProvider } from '@see-ui/core';
import CartPage from '@pages/secure/Pharmacy/CartPage';
import LoginPageCCNCSSO from '@pages/public/Login/LoginPageCCNCSSO';

const LoginPage = lazy(() => import('@pages/public/Login/LoginPage'));
const LoginPageOKTA = lazy(() => import('@pages/public/Login/LoginPageOKTA'));
const LoginPageNCSSO = lazy(() => import('@pages/public/Login/LoginPageNCSSO'));
const LoginPageOHSSO = lazy(() => import('@pages/public/Login/LoginPageOHSSO'));
const LoginPageSSO = lazy(() => import('@pages/public/Login/LoginPageSSO'));
const ForgotUsernamePage = lazy(
  () => import('@pages/public/ForgotUsernamePage/ForgotUsernamePage')
);

const LogOutPageSSO = lazy(() => import('@pages/public/LogOutPageSSO'));

const ForgotPasswordPage = lazy(
  () => import('@pages/public/ForgotPasswordPage/ForgotPasswordPage')
);
const DashboardPage = lazy(
  () => import('@pages/secure/DashboardPage/DashboardPage')
);
const MemberListingLegacyPage = lazy(
  () => import('@pages/secure/MemberListing/MemberListingLegacy')
);

const MemberListingRosterApiPage = lazy(
  () => import('@pages/secure/MemberListing/MemberListing')
);
const ChangePasswordPage = lazy(
  () => import('@pages/secure/ChangePasswordPage/ChangePasswordPage')
);

const RestPasswordPage = lazy(
  () =>
    import(
      '@pages/public/ForgotPasswordPage/ChangePasswordPage/ChangePasswordPage'
    )
);
const ToolsResourcesPage = lazy(
  () => import('@pages/secure/ToolsResources/ToolsResources')
);
const AdminPage = lazy(() => import('@pages/secure/Admin/Admin'));
const TutorialsPage = lazy(
  () => import('@pages/secure/Tutorials/TutorialsPage')
);
const CIIDashboard = lazy(
  () => import('@pages/secure/CIIDashboard/CIIDashboard')
);
const MedicalClaimDetails = lazy(
  () => import('@pages/secure/Claims/MedicalClaimDetailsOverviewPage')
);
const HealthHistoryPage = lazy(
  () => import('@pages/secure/HealthHistory/HealthHistory')
);
const Documents = lazy(() => import('@pages/secure/DocUpload/DocUpload'));
const IDCards = lazy(() => import('@pages/secure/ViewIDCard/ViewIDCard'));
const ChangePCP = lazy(() => import('@pages/secure/ChangePCP/ChangePCP'));
const Settings = lazy(() => import('@pages/secure/Admin/CompanySettings'));
const CareNotes = lazy(() => import('@pages/secure/CareNotes/CareNotes'));
const Claims = lazy(() => import('@pages/secure/Claims'));
const CommunicationsPage = lazy(
  () => import('@pages/secure/Communications/CommunicationsPage')
);
const AddMedications = lazy(
  () => import('@pages/secure/Medications/AddMedications')
);
const MemberActions = lazy(
  () => import('@pages/secure/MemberActions/MemberActions')
);
const MemberPage = lazy(() => import('@pages/secure/MemberPage'));
const RRTForm = lazy(() => import('@pages/secure/MemberPage/RRTForm'));

const UManagement = lazy(
  () => import('@pages/secure/U-Management/U-Management')
);
const ManageInternalUsers = lazy(
  () => import('@pages/secure/ManageInternalUsers/ManageInternalUsers')
);
const Assessments = lazy(() => import('@pages/secure/Assessment/Assessment'));
const CareGaps = lazy(() => import('@pages/secure/CareGaps/CareGaps'));
const CarePlan = lazy(() => import('@pages/secure/CarePlan/CarePlan'));
const CareTeam = lazy(() => import('@pages/secure/CareTeam/CareTeam'));
const Conditions = lazy(() => import('@pages/secure/Conditions/Conditions'));
const Grievances = lazy(() => import('@pages/secure/Grievances/Grievances'));
const MedicationsPage = lazy(
  () => import('@pages/secure/Medications/MedicationsPage')
);
const MemberOverview = lazy(
  () => import('@pages/secure/MemberOverview/MemberOverview')
);
const Questionnaire = lazy(
  () => import('@pages/secure/Assessment/AddAssessment/QuestionniarePage')
);
const Onetimetest = lazy(
  () =>
    import('@pages/public/ForgotPasswordPage/ChangePasswordPage/OneTimeCode')
);

const Authentication = lazy(
  () =>
    import('@pages/public/ForgotPasswordPage/ChangePasswordPage/Authentication')
);
const PasswordExpiry = lazy(
  () =>
    import('@pages/public/ForgotPasswordPage/ChangePasswordPage/PasswordExpiry')
);

const PasswordConfirmation = lazy(
  () =>
    import(
      '@pages/public/ForgotPasswordPage/ChangePasswordPage/PasswordConfirmation'
    )
);

const UploadUsers = lazy(
  () => import('@pages/secure/Admin/BulkUserUploadTemplate')
);

const SelfRegistration = lazy(
  () => import('@pages/public/SelfRegistration/SelfRegistration')
);

const ContactUs = lazy(() => import('@pages/secure/ContactUs/ContactUs'));

const SurveyCreation = lazy(
  () => import('@pages/secure/Survey/SurveyCreation')
);
const NewEnrollmentPage = lazy(
  () => import('@pages/secure/Enrollment/NewEnrollment')
);
const NewEnrollmentConfirmation = lazy(
  () => import('@pages/secure/Enrollment/NewEnrollmentConfirmation')
);

const Calendar = lazy(
  () => import('@pages/secure/DashboardPage/CalendarComponent')
);

const AllPrescriptions = lazy(
  () => import('@pages/secure/Pharmacy/AllPrescriptions')
);
const Pharmacy = lazy(() => import('@pages/secure/Pharmacy/PharmacyDashboard'));

const isVirtualPath = window.location.pathname.startsWith('/virtual');

const Survey = lazy(() => import('@pages/secure/Survey/Survey'));
const SurveyListing = lazy(() => import('@pages/secure/Survey/SurveyListing'));
function App() {
  const getQueryParams = () => {
    const urlParams = new URLSearchParams(window.location.search);
    if (
      window.location.pathname === '/change-password' &&
      window.location.search
    ) {
      const urlSplit = window.location.search.substring(1).split('&');
      const encryptedUserId = urlSplit[0].split('=')[1];
      const flow = urlSplit[1].split('=')[1];
      if (encryptedUserId) {
        sessionStorage.setItem('ENCRYPTED_USER_ID', encryptedUserId);
      }
      if (flow) {
        sessionStorage.setItem('FLOW', flow);
      }
    }
    if (urlParams.get('ciiEvolve') === 'true') {
      urlParams.forEach((value, key) => {
        sessionStorage.setItem(key, value);
      });
    }
  };

  useEffect(() => {
    getQueryParams();
  }, []);

  return (
    <ThemeProvider>
      <ConfigProvider>
        <AuthProvider>
          <SpinnerProvider>
            <BrowserRouter basename={isVirtualPath ? '/virtual' : undefined}>
              <div>
                <IdleTimer />
                {/* <Layout title="Care360"> */}
                <Suspense fallback={<div>Loading...</div>}>
                  <Routes>
                    <Route
                      path="/"
                      element={<Navigate to={ROUTES.LOGIN} replace />}
                    />
                    <Route path={ROUTES.LOGIN} element={<LoginPage />} />
                    <Route
                      path={ROUTES.LOGIN_OKTA}
                      element={<LoginPageOKTA />}
                    />
                    <Route
                      path={ROUTES.LOGIN_NC_SSO}
                      element={<LoginPageNCSSO />}
                    />
                    <Route
                      path={ROUTES.LOGIN_CCNC_SSO}
                      element={<LoginPageCCNCSSO />}
                    />
                    <Route
                      path={ROUTES.LOGIN_OH_SSO}
                      element={<LoginPageOHSSO />}
                    />
                    <Route path={ROUTES.SSO_ENTRY} element={<LoginPageSSO />} />
                    <Route
                      path={ROUTES.FORGOTUSERNAME}
                      element={<ForgotUsernamePage />}
                    />
                    <Route
                      path={ROUTES.FORGOTPACCESSCODE}
                      element={<ForgotPasswordPage />}
                    />
                    <Route
                      path={ROUTES.CHANGEACCESSCODE}
                      element={<ChangePasswordPage />}
                    />
                    <Route path={ROUTES.PHARMACY} element={<Pharmacy />} />
                    <Route
                      path={ROUTES.ONETIMECODE}
                      element={<Onetimetest />}
                    />
                    <Route
                      path={ROUTES.AUTHENTICATION}
                      element={<Authentication />}
                    />
                    <Route
                      path={ROUTES.PASSWORDEXPIRY}
                      element={<PasswordExpiry />}
                    />

                    <Route
                      path={ROUTES.ACCESSCODECONFIRMATION}
                      element={<PasswordConfirmation />}
                    />
                    <Route
                      path={ROUTES.RESETACCESSCODE}
                      element={<RestPasswordPage />}
                    />
                    <Route
                      path={ROUTES.SELFREGISTRATION}
                      element={<SelfRegistration />}
                    />
                    <Route
                      path={ROUTES.SSO_LOGOUT}
                      element={<LogOutPageSSO />}
                    />

                    <Route path={ROUTES.CONTACTUS} element={<ContactUs />} />
                    <Route
                      path={ROUTES.DASHBOARD}
                      element={<DashboardPage />}
                    />
                    <Route
                      path={ROUTES.MEMBERLISTINGLEGACY}
                      element={<MemberListingLegacyPage />}
                    />

                    <Route
                      path={ROUTES.MEMBERLISTINGROSTERAPI}
                      element={<MemberListingRosterApiPage />}
                    />
                    <Route
                      path={ROUTES.HEALTH_HISTORY}
                      element={<HealthHistoryPage />}
                    />
                    <Route
                      path={ROUTES.ADDMEDICATIONS}
                      element={<AddMedications />}
                    />
                    <Route
                      path={ROUTES.MEMBERPAGE}
                      element={
                        <MemberProvider>
                          <MemberPage />
                        </MemberProvider>
                      }
                    >
                      <Route
                        path="memberoverview"
                        element={<MemberOverview />}
                      />
                      <Route path="assessments" element={<Assessments />} />
                      <Route path="careactions" element={<MemberActions />} />
                      <Route path="notes" element={<CareNotes />} />
                      <Route path="rrtform" element={<RRTForm />} />
                      <Route path="grievances" element={<Grievances />} />
                      <Route path="claims" element={<Claims />} />
                      <Route path="UManagement" element={<UManagement />} />
                      <Route path="conditions" element={<Conditions />} />
                      <Route path="careTeam" element={<CareTeam />} />
                      <Route path="carePlan" element={<CarePlan />} />
                      <Route path="careGaps" element={<CareGaps />} />
                      <Route path="documents" element={<Documents />} />
                      <Route path="idcards" element={<IDCards />} />
                      <Route path="changepcp" element={<ChangePCP />} />
                      <Route path="pharmacy" element={<Pharmacy />} />
                      <Route path="medications" element={<MedicationsPage />} />
                      <Route
                        path="healthcharts"
                        element={<HealthHistoryPage />}
                      />
                      <Route path="careActions" element={<MemberActions />} />
                      <Route
                        path="communications"
                        element={<CommunicationsPage />}
                      />
                      <Route
                        index
                        element={<Navigate to="memberoverview" replace />}
                      />
                    </Route>
                    <Route
                      path={ROUTES.TOOLSRESOURCES}
                      element={<ToolsResourcesPage />}
                    />
                    <Route path={ROUTES.ADMIN} element={<AdminPage />} />
                    <Route
                      path={ROUTES.TUTORIALS}
                      element={<TutorialsPage />}
                    />
                    <Route
                      path={ROUTES.CIIDASHBOARD}
                      element={<CIIDashboard />}
                    />
                    <Route
                      path={ROUTES.MANAGEINTERNALUSERS}
                      element={<ManageInternalUsers />}
                    />
                    <Route path={ROUTES.SETTINGS} element={<Settings />} />
                    <Route
                      path={ROUTES.UPLOADUSERS}
                      element={<UploadUsers />}
                    />
                    <Route
                      path="survey-creation"
                      element={<SurveyCreation />}
                    />
                    <Route path="survey" element={<Survey />} />
                    <Route path="survey-listing" element={<SurveyListing />} />
                    <Route
                      path={ROUTES.NEWENROLLMENT}
                      element={<NewEnrollmentPage />}
                    />
                    <Route
                      path={ROUTES.NEWENROLLMENTCONFIRMATION}
                      element={<NewEnrollmentConfirmation />}
                    />
                    <Route path={ROUTES.CALENDAR} element={<Calendar />} />
                    <Route
                      path={ROUTES.ALLPRESCRIPTIONS}
                      element={<AllPrescriptions />}
                    />
                    <Route
                      path="/pharmacy-order-review"
                      element={<CartPage />}
                    />
                  </Routes>
                </Suspense>
                {/* </Layout> */}
              </div>
            </BrowserRouter>
            <Spinner />
          </SpinnerProvider>
        </AuthProvider>
      </ConfigProvider>
    </ThemeProvider>
  );
}

export default App;
